import * as React from 'react';
import { KeyRound, Loader2, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import RegionSelect from '@/components/common/RegionSelect';
import { useCurrencyRegions } from '@/hooks/useCurrencyRegions';
import { defaultRegion, regionBySlug } from '@/lib/regions';
import {
  LicenseTier,
  STANDARD_TIER,
  useLicensePrices,
  useLicensing,
} from '@/hooks/useLicensing';
import { apiMessage, today } from '@/lib/pricing';

const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });

function fee(row: { monthly_price: string; currency: string }) {
  return `${Number(row.monthly_price).toLocaleString()} ${row.currency}`;
}

export default function LicenceFeeCard() {
  const license = useLicensing();
  const { add, remove } = useLicensePrices();
  const regions = useCurrencyRegions();

  // Which tier is being priced comes from the card you clicked, so it is never
  // something the form has to ask you to pick again.
  const [editing, setEditing] = React.useState<LicenseTier | null>(null);
  const [amount, setAmount] = React.useState('');
  const [region, setRegion] = React.useState('');
  const [date, setDate] = React.useState(today());
  const [note, setNote] = React.useState('');

  const [shown, setShown] = React.useState('');

  React.useEffect(() => {
    if (shown) return;
    const start = defaultRegion(regions.data?.regions ?? []);
    if (start) setShown(start.slug);
  }, [shown, regions.data]);

  const all = regions.data?.regions ?? [];
  const fallback = defaultRegion(all);
  const here = regionBySlug(all, shown);
  // A region priced in the default's currency inherits what it does not set,
  // which is the backend's rule; showing it keeps the page honest about what
  // a customer in this region actually pays.
  const inheritsDefault = Boolean(
    here && fallback && here.slug !== fallback.slug && here.currency === fallback.currency,
  );

  const standard = (license.data?.tiers ?? []).find((tier) => tier.slug === STANDARD_TIER);

  /** What this tier charges in the region on show, and where that came from. */
  const charged = (tier: LicenseTier) => {
    const own = tier.current[shown];
    if (own) return { row: own, from: null as string | null };
    const shelf = standard?.current[shown];
    if (shelf && tier.slug !== STANDARD_TIER) return { row: shelf, from: 'small business' };
    if (inheritsDefault && fallback) {
      const theirs = tier.current[fallback.slug] ?? standard?.current[fallback.slug];
      if (theirs) return { row: theirs, from: fallback.name };
    }
    return { row: null, from: null };
  };

  const open = (tier: LicenseTier) => {
    setEditing(tier);
    setAmount('');
    setRegion(shown || defaultRegion(regions.data?.regions ?? [])?.slug || '');
    setDate(today());
    setNote('');
  };

  const save = () => {
    if (!editing) return;
    add.mutate(
      {
        tier: editing.slug,
        monthly_price: amount,
        region,
        effective_date: date,
        notes: note,
      },
      {
        onSuccess: () => {
          setEditing(null);
          toast.success(`${editing.name} fee set`);
        },
        onError: (error) => toast.error(apiMessage(error)),
      },
    );
  };

  const tierCard = (tier: LicenseTier) => {
    const { row, from } = charged(tier);
    const history = tier.prices.filter((price) => price.region === shown);
    return (
      <div key={tier.slug} className="rounded-md border p-3" data-testid={`license-tier-${tier.slug}`}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-medium">{tier.name}</div>
            <p className="mt-1 text-xs text-muted-foreground">{tier.blurb}</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => open(tier)}
            data-testid={`license-fee-set-${tier.slug}`}
          >
            Set fee
          </Button>
        </div>
        <div className="mt-3" data-testid={`license-tier-${tier.slug}-current`}>
          {row ? (
            <div
              className={from ? 'text-lg font-semibold text-muted-foreground' : 'text-lg font-semibold'}
              data-testid={from ? `license-tier-${tier.slug}-inherited` : undefined}
            >
              {fee(row)}
              <span className="text-xs font-normal text-muted-foreground"> /mo</span>
              {from && <p className="text-xs font-normal">from {from}</p>}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No fee in {here?.name ?? 'this region'}, so it cannot be invoiced
            </p>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {tier.organizations} organisation{tier.organizations === 1 ? '' : 's'}
        </p>
        {history.length > 0 && (
          <table className="mt-2 w-full text-xs">
            <tbody>
              {history.map((price) => (
                <tr key={price.id} className="border-t" data-testid={`license-fee-row-${price.id}`}>
                  <td className="py-1">{day(price.effective_date)}</td>
                  <td className="py-1">{fee(price)}</td>
                  <td className="py-1 text-right">
                    <Button size="sm" variant="ghost" onClick={() => remove.mutate(price.id)} title="Remove">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <Card data-testid="license-fee-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            Servers customers run themselves
          </CardTitle>
          <div className="w-64">
            <RegionSelect
              value={shown}
              onChange={setShown}
              regions={all}
              testId="licence-fees-region"
            />
          </div>
        </div>
        <CardDescription>
          The monthly licence fee for a Bailey server on the customer's own
          machine. Every organisation sits on one of three tiers. A server keeps
          the fee it started on; moving a customer to another tier reprices
          everything they run. A tier with no fee of its own charges the small
          business fee. A negotiated plan overrides both, on the Enterprise plans page.
          {inheritsDefault && fallback
            ? ` ${here?.name} is priced in the same currency as ${fallback.name}, so anything
                it does not set is inherited from there.`
            : ''}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {license.isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <div className="grid gap-3 md:grid-cols-3" data-testid="license-tier-list">
            {(license.data?.tiers ?? []).map(tierCard)}
          </div>
        )}
      </CardContent>

      <Dialog open={editing !== null} onOpenChange={(isOpen) => { if (!isOpen) setEditing(null); }}>
        <DialogContent data-testid="license-fee-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.name} licence fee</DialogTitle>
            <DialogDescription>
              Servers already running keep the fee they started on. This one applies to
              servers connected from that date.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="license-fee-price">Monthly fee</Label>
              <Input
                id="license-fee-price"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="license-fee-price"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Region</Label>
              <RegionSelect
                value={region}
                onChange={setRegion}
                regions={regions.data?.regions ?? []}
                testId="license-fee-region"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="license-fee-date">From</Label>
              <Input
                id="license-fee-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="license-fee-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="license-fee-note">Note</Label>
              <Input
                id="license-fee-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="license-fee-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              onClick={save}
              disabled={add.isPending || !amount || !region || !date}
              data-testid="license-fee-save"
            >
              {add.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Set licence fee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
